/**
 * InboxConversationPane — Full thread reading pane for MailConversation.
 *
 * Uses fetchConversation() which returns all emails pre-loaded in the thread.
 * No secondary fetch needed (unlike InboxThreadPane which starts with emailId).
 * Most recent message is expanded by default; earlier messages are collapsed.
 */

import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import {
  Flag, ArrowBendUpLeft, ArrowBendDoubleUpLeft, ArrowBendUpRight,
  Paperclip, DownloadSimple, Scales, Gavel, Calendar, CurrencyDollar,
  Printer, FolderSimplePlus,
} from '@phosphor-icons/react';
import {
  fetchConversation, downloadAttachment, saveAttachmentToMatter,
  type ConversationDetail,
} from '@/lib/inboxApi';
import { SafeEmailHtml } from './SafeEmailHtml';
import { ReplyPane } from './ReplyPane';

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
}

interface Props {
  conversationId: string;
  onFollowUp: () => void;
  onLinkMatter: () => void;
  onAcceptSuggestion?: (matterId: string) => void;
  onDismissSuggestion?: () => void;
  onCreateTask?: (matterId: string, matterName: string) => void;
  onCreateHearingNote?: (matterId: string, matterName: string) => void;
  onLogTime?: (matterId: string, matterName: string) => void;
  onGenerateFeeEntry?: (matterId: string, matterName: string) => void;
  onSaveToDocuments?: (matterId: string, matterName: string) => void;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function senderName(addr: string): string {
  const m = addr.match(/^([^<]+)<[^>]+>$/);
  return m ? m[1]!.trim() : addr;
}

function fileSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type ConvEmail = ConversationDetail['emails'][number];

function CollapsedMessageCard({ msg }: { msg: ConvEmail }) {
  return (
    <div style={{
      padding: '8px 20px', borderBottom: '1px solid var(--border-subtle)',
      display: 'flex', alignItems: 'center', gap: 12, cursor: 'default',
      background: 'var(--surface-page)',
    }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: msg.isRead ? 'transparent' : '#2563EB', flexShrink: 0 }} />
      <div style={{
        fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13,
        fontWeight: msg.isRead ? 400 : 600, color: 'var(--text-secondary)', flex: 1,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {senderName(msg.fromAddress)}
        {msg.snippet && (
          <span style={{ fontWeight: 400, color: 'var(--text-subtle)', marginLeft: 6 }}>
            {msg.snippet}
          </span>
        )}
      </div>
      <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11, color: 'var(--text-subtle)', flexShrink: 0 }}>
        {fmtDate(msg.receivedAt ?? msg.sentAt)}
      </span>
    </div>
  );
}

function ExpandedMessageCard({
  msg, isLast, onReply, matter, onSaveAttachment, savingAttId,
}: {
  msg: ConvEmail;
  isLast: boolean;
  onReply: (emailId: string, mode: 'reply' | 'replyAll' | 'forward') => void;
  matter?: { id: string; caseNumber: string } | null;
  onSaveAttachment?: (emailId: string, attId: string, filename: string) => void;
  savingAttId?: string | null;
}) {
  const hasBody = msg.bodyFetched && (msg.bodyHtml || msg.bodyText);

  return (
    <div style={{
      border: '1px solid var(--border-default)', borderRadius: 8, margin: '0 16px 12px',
      background: 'var(--surface-card)', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ padding: '14px 18px 10px', borderBottom: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{
              fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 14, fontWeight: 600,
              color: 'var(--text-primary)',
            }}>
              {senderName(msg.fromAddress)}
            </div>
            <div style={{
              fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-subtle)', marginTop: 2,
            }}>
              to {msg.toAddresses.join(', ')}
              {msg.ccAddresses.length > 0 && <span style={{ marginLeft: 6 }}>cc {msg.ccAddresses.join(', ')}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11, color: 'var(--text-subtle)' }}>
              {fmtDate(msg.receivedAt ?? msg.sentAt)}
            </span>
            {isLast && (
              <>
                <button
                  onClick={() => onReply(msg.id, 'reply')}
                  title="Reply"
                  style={{
                    padding: '4px 8px', border: '1px solid var(--border-default)',
                    borderRadius: 5, background: 'var(--surface-card)',
                    color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                    fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12,
                  }}
                >
                  <ArrowBendUpLeft size={13} /> Reply
                </button>
                <button
                  onClick={() => onReply(msg.id, 'replyAll')}
                  title="Reply All"
                  style={{
                    padding: '4px 7px', border: '1px solid var(--border-default)',
                    borderRadius: 5, background: 'var(--surface-card)',
                    color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center',
                  }}
                >
                  <ArrowBendDoubleUpLeft size={13} />
                </button>
                <button
                  onClick={() => onReply(msg.id, 'forward')}
                  title="Forward"
                  style={{
                    padding: '4px 7px', border: '1px solid var(--border-default)',
                    borderRadius: 5, background: 'var(--surface-card)',
                    color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center',
                  }}
                >
                  <ArrowBendUpRight size={13} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '16px 18px', minHeight: 80 }}>
        {!hasBody ? (
          <div>
            {msg.snippet && (
              <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 10 }}>
                {msg.snippet}
              </div>
            )}
            <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-subtle)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b', flexShrink: 0 }} />
              {msg.snippet
                ? 'Full message preview only — reconnect your mailbox to load the complete email.'
                : 'Message body could not be loaded — reconnect your mailbox to retry.'}
            </div>
          </div>
        ) : msg.bodyHtml ? (
          <SafeEmailHtml html={msg.bodyHtml} fontSize={13} />
        ) : (
          <pre style={{
            fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13,
            lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            color: 'var(--text-primary)', margin: 0,
          }}>
            {msg.bodyText}
          </pre>
        )}
      </div>

      {/* Attachments */}
      {msg.attachments.length > 0 && (
        <div style={{ padding: '10px 18px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {msg.attachments.map((att) => (
            <div
              key={att.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px',
                border: '1px solid var(--border-default)', borderRadius: 5,
                background: 'var(--surface-page)',
                fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-secondary)',
              }}
            >
              <Paperclip size={12} />
              <span>{att.filename}</span>
              <span style={{ color: 'var(--text-subtle)' }}>{fileSize(att.sizeBytes)}</span>
              {matter && onSaveAttachment && (
                <button
                  onClick={() => onSaveAttachment(msg.id, att.id, att.filename)}
                  disabled={savingAttId === att.id}
                  title={`Save to ${matter.caseNumber}`}
                  style={{ background: 'none', border: 'none', cursor: savingAttId === att.id ? 'wait' : 'pointer', color: '#2563eb', display: 'flex', alignItems: 'center', padding: 0, opacity: savingAttId === att.id ? 0.5 : 1 }}
                >
                  <FolderSimplePlus size={13} />
                </button>
              )}
              <button
                onClick={() => downloadAttachment(msg.id, att.id, att.filename).catch(() => toast.error('Download failed'))}
                title="Download"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subtle)', display: 'flex', alignItems: 'center', padding: 0 }}
              >
                <DownloadSimple size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main pane ────────────────────────────────────────────────

export function InboxConversationPane({
  conversationId, onFollowUp, onLinkMatter, onAcceptSuggestion, onDismissSuggestion,
  onCreateTask, onCreateHearingNote, onLogTime,
  onGenerateFeeEntry, onSaveToDocuments,
}: Props) {
  const queryClient = useQueryClient();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const [replyTarget, setReplyTarget] = useState<{ emailId: string; mode: 'reply' | 'replyAll' | 'forward' } | null>(null);

  const { data: conv, isLoading } = useQuery<ConversationDetail>({
    queryKey: ['inbox-conversation', conversationId],
    queryFn: () => fetchConversation(conversationId),
    enabled: !!conversationId,
  });

  const [savingAttId, setSavingAttId] = useState<string | null>(null);
  const saveAttMutation = useMutation({
    mutationFn: ({ emailId, attId }: { emailId: string; attId: string; filename: string }) =>
      saveAttachmentToMatter(emailId, attId, conv!.matter!.id),
    onMutate: ({ attId }) => setSavingAttId(attId),
    onSuccess: (_d, { filename }) => toast.success(`"${filename}" saved to ${conv!.matter!.caseNumber}`),
    onError: () => toast.error('Could not save to matter'),
    onSettled: () => setSavingAttId(null),
  });
  function handleSaveAttachment(emailId: string, attId: string, filename: string) {
    if (!conv?.matter) { toast.error('Link this thread to a matter first'); return; }
    saveAttMutation.mutate({ emailId, attId, filename });
  }

  function handlePrintThread() {
    if (!conv) return;
    const win = window.open('', '_blank', 'width=820,height=900');
    if (!win) { toast.error('Pop-up blocked — allow pop-ups to print'); return; }
    const parts = conv.emails.map((m) => {
      const body = m.bodyHtml
        ? DOMPurify.sanitize(m.bodyHtml)
        : `<pre style="white-space:pre-wrap;font-family:inherit">${escapeHtml(m.bodyText ?? '')}</pre>`;
      return `<div class="msg"><div class="meta"><b>From:</b> ${escapeHtml(m.fromAddress)}</div>`
        + `<div class="meta"><b>To:</b> ${escapeHtml(m.toAddresses.join(', '))}</div>`
        + `<div class="meta"><b>Date:</b> ${escapeHtml(fmtDate(m.receivedAt ?? m.sentAt))}</div><hr/>${body}</div>`;
    }).join('<div class="sep"></div>');
    win.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(conv.subject)}</title>`
      + `<style>body{font-family:'IBM Plex Sans',Arial,sans-serif;color:#111;padding:32px;line-height:1.6}`
      + `.h{font-size:20px;font-weight:600;margin:0 0 16px}.meta{font-size:12px;color:#555;margin-bottom:4px}`
      + `.msg{margin-bottom:24px}.sep{border-top:2px solid #eee;margin:20px 0}hr{border:none;border-top:1px solid #ddd;margin:12px 0}</style>`
      + `</head><body><div class="h">${escapeHtml(conv.subject)}</div>${parts}</body></html>`,
    );
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 250);
  }

  if (isLoading || !conv) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 14, color: 'var(--text-subtle)' }}>
          Loading…
        </div>
      </div>
    );
  }

  const emails = conv.emails;
  const lastEmail = emails[emails.length - 1];
  const replyEmail = replyTarget ? emails.find((e) => e.id === replyTarget.emailId) ?? null : null;

  function toggleExpanded(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Invalidate conversation list when actions taken
  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['inbox-conversations'] });
    void queryClient.invalidateQueries({ queryKey: ['folder-counts'] });
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Thread header */}
      <div style={{
        padding: '14px 20px', borderBottom: '1px solid var(--border-default)',
        flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'var(--surface-card)',
      }}>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <h2 style={{
            fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 16, fontWeight: 600,
            color: 'var(--text-primary)', margin: 0,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {conv.subject}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            {conv.matter && (
              <span style={{
                fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11, fontWeight: 500,
                color: '#1d4ed8', background: '#eff6ff', borderRadius: 3, padding: '2px 6px',
              }}>
                {conv.matter.caseNumber} — {conv.matter.caseName}
              </span>
            )}
            {conv.hasHearingRef && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#7c3aed', fontFamily: "'IBM Plex Sans', sans-serif" }}>
                <Gavel size={11} weight="fill" /> Hearing reference
              </span>
            )}
            {conv.hasDeadline && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#dc2626', fontFamily: "'IBM Plex Sans', sans-serif" }}>
                <Calendar size={11} weight="fill" /> Deadline
              </span>
            )}
            {conv.isBillingRelevant && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#059669', fontFamily: "'IBM Plex Sans', sans-serif" }}>
                <CurrencyDollar size={11} weight="fill" /> Billing
              </span>
            )}
          </div>
        </div>

        {/* Action toolbar */}
        {/* COMM-2: the conversation-level "Archive" button was removed — there is no
            conversation-archive backend (only per-email archive), so it did nothing. Rather than
            fake a local-only archived state, the dead action is removed. */}
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button
            onClick={handlePrintThread}
            title="Print thread"
            style={{
              padding: '5px 7px', border: '1px solid var(--border-default)', borderRadius: 6,
              background: 'var(--surface-card)', cursor: 'pointer',
              color: 'var(--text-secondary)', display: 'flex', alignItems: 'center',
            }}
          >
            <Printer size={14} />
          </button>
          <button
            onClick={() => { onFollowUp(); invalidate(); }}
            title="Mark for follow-up"
            style={{
              padding: '5px 7px', border: '1px solid var(--border-default)', borderRadius: 6,
              background: 'var(--surface-card)', cursor: 'pointer',
              color: 'var(--text-secondary)', display: 'flex', alignItems: 'center',
            }}
          >
            <Flag size={14} />
          </button>
          <button
            onClick={onLinkMatter}
            title="Link to matter"
            style={{
              padding: '5px 7px', border: '1px solid var(--border-default)', borderRadius: 6,
              background: conv.matter ? '#eff6ff' : 'var(--surface-card)',
              cursor: 'pointer',
              color: conv.matter ? '#1d4ed8' : 'var(--text-secondary)',
              display: 'flex', alignItems: 'center',
            }}
          >
            <Scales size={14} />
          </button>
          {conv.matter && onCreateTask && (
            <button
              onClick={() => onCreateTask(conv.matter!.id, conv.matter!.caseName)}
              title="Create task"
              style={{
                padding: '5px 9px', border: '1px solid var(--border-default)', borderRadius: 6,
                background: 'var(--surface-card)', cursor: 'pointer',
                color: 'var(--text-secondary)', fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12,
              }}
            >
              + Task
            </button>
          )}
        </div>
      </div>

      {/* Suggested matter link — the classifier matched this thread to a matter
          but it is not yet confirmed. One click accepts and links the whole thread. */}
      {conv.linkStatus === 'suggested' && conv.matter && (
        <div style={{
          margin: '12px 16px 0', padding: '10px 14px', borderRadius: 8,
          background: '#f0fdf4', border: '1px solid #bbf7d0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11, fontWeight: 600, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Suggested matter link
            </div>
            <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, color: '#166534', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {conv.matter.caseNumber} — {conv.matter.caseName}
              {typeof conv.linkConfidence === 'number' && (
                <span style={{ color: '#16a34a', marginLeft: 6 }}>· {Math.round(conv.linkConfidence * 100)}% match</span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {onDismissSuggestion && (
              <button
                onClick={onDismissSuggestion}
                title="Not this matter — stop suggesting it"
                style={{
                  padding: '6px 10px', borderRadius: 6, border: 'none', background: 'none', cursor: 'pointer',
                  fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-subtle)',
                }}
              >
                Dismiss
              </button>
            )}
            <button
              onClick={onLinkMatter}
              style={{
                padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border-strong)',
                background: 'var(--surface-card)', cursor: 'pointer',
                fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-secondary)',
              }}
            >
              Change
            </button>
            <button
              onClick={() => onAcceptSuggestion?.(conv.matter!.id)}
              style={{
                padding: '6px 14px', borderRadius: 6, border: 'none',
                background: '#16a34a', cursor: 'pointer',
                fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, fontWeight: 600, color: 'white',
              }}
            >
              Accept
            </button>
          </div>
        </div>
      )}

      {/* Thread messages */}
      <div style={{ flex: 1, overflow: 'auto', paddingTop: 12 }}>
        {emails.map((email, i) => {
          const isLast = i === emails.length - 1;
          // Last message is always expanded; others expand on toggle
          const isExpanded = isLast || expandedIds.has(email.id);

          if (isExpanded) {
            return (
              <ExpandedMessageCard
                key={email.id} msg={email} isLast={isLast}
                onReply={(emailId, mode) => setReplyTarget({ emailId, mode })}
                matter={conv.matter}
                onSaveAttachment={handleSaveAttachment}
                savingAttId={savingAttId}
              />
            );
          }

          return (
            <div key={email.id} onClick={() => toggleExpanded(email.id)} style={{ cursor: 'pointer' }}>
              <CollapsedMessageCard msg={email} />
            </div>
          );
        })}

        {/* Legal action footer when matter linked */}
        {conv.matter && lastEmail && (
          <div style={{
            margin: '8px 16px 16px', padding: '10px 14px',
            border: '1px solid var(--border-default)', borderRadius: 8,
            background: 'var(--surface-page)', display: 'flex', gap: 8, flexWrap: 'wrap',
          }}>
            {onLogTime && (
              <button
                onClick={() => onLogTime(conv.matter!.id, conv.matter!.caseName)}
                style={{
                  padding: '4px 10px', border: '1px solid var(--border-default)', borderRadius: 5,
                  background: 'var(--surface-card)', cursor: 'pointer',
                  fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-secondary)',
                }}
              >
                Log time
              </button>
            )}
            {onGenerateFeeEntry && (
              <button
                onClick={() => onGenerateFeeEntry(conv.matter!.id, conv.matter!.caseName)}
                style={{
                  padding: '4px 10px', border: '1px solid var(--border-default)', borderRadius: 5,
                  background: 'var(--surface-card)', cursor: 'pointer',
                  fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-secondary)',
                }}
              >
                Fee entry
              </button>
            )}
            {onCreateHearingNote && (
              <button
                onClick={() => onCreateHearingNote(conv.matter!.id, conv.matter!.caseName)}
                style={{
                  padding: '4px 10px', border: '1px solid var(--border-default)', borderRadius: 5,
                  background: 'var(--surface-card)', cursor: 'pointer',
                  fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-secondary)',
                }}
              >
                Hearing note
              </button>
            )}
            {onSaveToDocuments && (
              <button
                onClick={() => onSaveToDocuments(conv.matter!.id, conv.matter!.caseName)}
                style={{
                  padding: '4px 10px', border: '1px solid var(--border-default)', borderRadius: 5,
                  background: 'var(--surface-card)', cursor: 'pointer',
                  fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-secondary)',
                }}
              >
                Save to docs
              </button>
            )}
          </div>
        )}
      </div>

      {/* Reply / Reply-All / Forward composer — pinned footer below the thread */}
      {replyTarget && replyEmail && (
        <div style={{ borderTop: '1px solid var(--border-default)', flexShrink: 0, padding: '12px 24px', maxHeight: '55%', overflow: 'auto' }}>
          <ReplyPane
            email={replyEmail}
            mode={replyTarget.mode === 'replyAll' ? 'reply-all' : replyTarget.mode}
            onClose={() => setReplyTarget(null)}
            onSent={() => {
              setReplyTarget(null);
              queryClient.invalidateQueries({ queryKey: ['inbox-conversation', conversationId] });
            }}
          />
        </div>
      )}
    </div>
  );
}
